import React, { useState } from 'react'
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  CardHeader,
  styled,
  Alert,
  CircularProgress,
  IconButton,
  Snackbar,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material'
import { IconCopy } from '@tabler/icons-react'
import axiosInstance from '../../../../utils/axiosInstance'

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: '8px',
  overflow: 'visible',
  boxShadow: 'none',
  border: '1px solid #e0e0e0',
  width: '100%',
}))

const StyledCardHeader = styled(CardHeader)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  '& .MuiCardHeader-title': {
    fontSize: '1rem',
    fontWeight: 600,
  },
}))

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3),
  '&:last-child': {
    paddingBottom: theme.spacing(3),
  },
}))

const VariantCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: '8px',
  border: '1px solid #e0e0e0',
  boxShadow: 'none',
  position: 'relative',
}))

const VariantContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[50],
  borderRadius: '4px',
  whiteSpace: 'pre-wrap',
  fontFamily: 'inherit',
  fontSize: '0.95rem',
  lineHeight: 1.6,
  position: 'relative',
}))

const CopyButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  backgroundColor: 'white',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  '&:hover': {
    backgroundColor: theme.palette.primary.light,
    color: 'white',
  },
}))

interface SocialAdProps {
  orderId: string
  hasAdDescription: boolean
}

interface Variant {
  text: string
  label: string
}

const DEFAULT_PROMPT = `Requirements:
- Create exactly 2 different variants
- Each variant should be engaging and optimized for social media
- Include relevant emojis to make the post more appealing
- Include 3-5 relevant hashtags at the end
- Keep each variant concise (80-120 words)
- Focus on the job benefits and what makes it attractive
- Use a friendly, professional tone

IMPORTANT: You must provide exactly 2 variants. Format your response EXACTLY as follows:

Variant 1:
[First social media post with emojis and hashtags]

Variant 2:
[Second social media post with emojis and hashtags]

Do not include any other text or explanations. Just provide the two variants.`

const SocialAd: React.FC<SocialAdProps> = ({ orderId, hasAdDescription }) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [variants, setVariants] = useState<Variant[]>([])
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [customPrompt, setCustomPrompt] = useState(DEFAULT_PROMPT)
  const [selectedModel, setSelectedModel] = useState<'gpt-5.1' | 'gpt-5-mini'>('gpt-5.1')

  const handleGenerateAd = async () => {
    setLoading(true)
    setError(null)
    setVariants([])

    try {
      const response = await axiosInstance.post('/generate-social-ad', {
        orderId,
        customPrompt: customPrompt.trim() || undefined,
        model: selectedModel,
      })

      const generatedVariants = response.data.variants || []
      setVariants(
        generatedVariants.map((text: string, index: number) => ({
          text,
          label: `Variant ${index + 1}`,
        }))
      )

      if (generatedVariants.length === 0) {
        setError('No variants were generated. Please try again.')
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error || err.message || 'Failed to generate social ad content'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleCopy = (text: string, variantLabel: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setSnackbarMessage(`${variantLabel} copied to clipboard!`)
      setSnackbarOpen(true)
    })
  }

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false)
  }

  return (
    <Box sx={{ width: '960px', maxWidth: '100%', margin: '0 auto', padding: 2 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        Social Ad Generator
      </Typography>

      <StyledCard>
        <StyledCardHeader title="Generate AI-Powered Social Media Ads" />
        <StyledCardContent>
          {!hasAdDescription ? (
            <Alert severity="warning">
              This order does not have an ad description. Please add an ad description to generate
              social ads.
            </Alert>
          ) : (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Generate engaging social media ad content for Meta and Instagram using AI. The
                generated content will include emojis and relevant hashtags.
              </Typography>

              <Box sx={{ mb: 3 }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
                    AI Model
                  </FormLabel>
                  <RadioGroup
                    row
                    value={selectedModel}
                    onChange={(e) => setSelectedModel(e.target.value as 'gpt-5.1' | 'gpt-5-mini')}
                  >
                    <FormControlLabel
                      value="gpt-5.1"
                      control={<Radio />}
                      label="GPT-5.1 (Best Quality)"
                    />
                    <FormControlLabel
                      value="gpt-5-mini"
                      control={<Radio />}
                      label="GPT-5 Mini (Faster)"
                    />
                  </RadioGroup>
                </FormControl>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Custom Prompt (Optional)
                </Typography>
                <TextField
                  multiline
                  rows={10}
                  fullWidth
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  placeholder="Enter custom instructions for the AI..."
                  variant="outlined"
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                    },
                  }}
                  helperText="Customize the AI instructions or leave as default. The job information will be automatically included."
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setCustomPrompt(DEFAULT_PROMPT)}
                  sx={{ mr: 1 }}
                >
                  Reset to Default
                </Button>
              </Box>

              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleGenerateAd}
                disabled={loading}
                sx={{ mb: 3 }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                    Generating...
                  </>
                ) : (
                  'Generate Social Ad for Meta and Instagram with AI'
                )}
              </Button>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {variants.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Generated Variants
                  </Typography>

                  {variants.map((variant, index) => (
                    <VariantCard key={index}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle1" fontWeight="600">
                            {variant.label}
                          </Typography>
                        </Box>
                        <VariantContent>
                          <CopyButton
                            size="small"
                            onClick={() => handleCopy(variant.text, variant.label)}
                            title="Copy to clipboard"
                          >
                            <IconCopy size={18} />
                          </CopyButton>
                          {variant.text}
                        </VariantContent>
                      </CardContent>
                    </VariantCard>
                  ))}

                  <Alert severity="info" sx={{ mt: 2 }}>
                    Click the copy icon on each variant to copy it to your clipboard.
                  </Alert>
                </Box>
              )}
            </>
          )}
        </StyledCardContent>
      </StyledCard>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  )
}

export default SocialAd
